import axiosInstance from './axiosConfig';
import { handleApiError } from '../../utils/errorHandling';

// クレジット残高の型定義
export interface CreditBalance {
  monthly: number;
  free: number;
  additional: number;
  total: number;
}

// 組織詳細情報の型定義
export interface OrganizationDetails {
  id: number;
  name: string;
  description: string | null;
  invitation_code: string | null;
  invitation_credit_amount: number;
  member_limit: number;
  monthly_token_limit: number;
  available_member_slots: number;
  remaining_tokens: number;
  credit_balance: CreditBalance;
}

// 組織メンバーの型定義
export interface OrganizationMember {
  id: number;
  name: string;
  email: string;
  role: 'leader' | 'member';
  photo_url: string | null;
  ai_usage_score: number;
  is_current_user: boolean;
}

// 招待中メンバーの型定義
export interface PendingInvitation {
  id: number;
  name: string | null;
  email: string;
  status: string;
  expires_at: string;
}

// 組織詳細取得のレスポンス
export interface OrganizationDetailsResponse {
  organization: OrganizationDetails;
  members: OrganizationMember[];
  pending_invitations?: PendingInvitation[];
}

// 契約ステータスの型定義
export interface SubscriptionStatus {
  plan: {
    name: string | null;
    started_at: string | null;
    is_active: boolean;
  };
  trial: {
    is_in_trial: boolean;
    has_expired: boolean;
    type: string | null;
    ends_at: string | null;
    usage_count: number;
    usage_limit: number;
  };
  credits: CreditBalance;
  usage: {
    tokens_used: number;
    tokens_limit: number;
    credits_used: number;
    credits_limit: number;
    members_count: number;
    members_limit: number;
  };
  features: Record<string, boolean>;
  has_stripe_customer?: boolean;
  subscription?: {
    cancel_at_period_end: boolean;
    current_period_end: number | null;
  };
  pause?: {
    is_paused: boolean;
    paused_at: string | null;
    pause_expires_at: string | null;
  };
}

/**
 * 契約ステータス情報を取得
 */
export const fetchSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
  try {
    const response = await axiosInstance.get('/organizations/manage/subscription-status');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '契約ステータスの取得に失敗しました');
  }
};

/**
 * Stripeカスタマーポータルセッションを作成
 */
export const createBillingPortalSession = async (): Promise<{ url: string }> => {
  try {
    const response = await axiosInstance.post('/organizations/manage/billing-portal');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'ポータルセッションの作成に失敗しました');
  }
};

/**
 * 組織詳細情報（メンバー一覧含む）を取得
 */
export const fetchOrganizationDetails = async (): Promise<OrganizationDetailsResponse> => {
  try {
    const response = await axiosInstance.get('/organizations/manage/details');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '組織情報の取得に失敗しました');
  }
};

/**
 * 組織名を更新
 * @param name 新しい組織名
 */
export const updateOrganizationName = async (name: string): Promise<{ id: number; name: string }> => {
  try {
    const response = await axiosInstance.put('/organizations/manage/name', { name });
    return response.data.organization;
  } catch (error) {
    throw handleApiError(error, '組織名の更新に失敗しました');
  }
};

/**
 * 招待コード設定を更新
 */
export const updateInvitationCode = async (
  invitationCode: string,
  invitationCreditAmount: number
): Promise<{ id: number; invitation_code: string | null; invitation_credit_amount: number }> => {
  try {
    const response = await axiosInstance.put('/organizations/manage/invitation-code', {
      invitation_code: invitationCode.trim() || null,
      invitation_credit_amount: invitationCreditAmount,
    });
    return response.data.organization;
  } catch (error) {
    throw handleApiError(error, '招待コード設定の更新に失敗しました');
  }
};

/**
 * メンバーのロールを更新
 * @param userId メンバーID
 * @param role 新しいロール
 */
export const updateMemberRole = async (userId: number, role: 'leader' | 'member'): Promise<void> => {
  try {
    await axiosInstance.put(`/organizations/manage/members/${userId}/role`, { role });
  } catch (error) {
    throw handleApiError(error, 'メンバーのロール更新に失敗しました');
  }
};

/**
 * メンバーを組織から削除
 * @param userId メンバーID
 */
export const removeMember = async (userId: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/organizations/manage/members/${userId}`);
  } catch (error) {
    throw handleApiError(error, 'メンバーの削除に失敗しました');
  }
};

/**
 * メンバーの名前とメールアドレスを更新
 * @param userId メンバーID
 * @param name 新しい名前
 * @param email 新しいメールアドレス
 * @returns 更新されたメンバー情報
 */
export const updateMemberInfo = async (
  userId: number, 
  name: string, 
  email: string
): Promise<{id: number; name: string; email: string; role: string}> => {
  try {
    const response = await axiosInstance.put(`/organizations/manage/members/${userId}/info`, {
      name,
      email
    });
    return response.data.member;
  } catch (error) {
    throw handleApiError(error, 'メンバー情報の更新に失敗しました');
  }
};

/**
 * 新しいメンバーを追加
 * @param name 名前
 * @param email メールアドレス
 * @param role ロール ('leader' または 'member')
 * @returns 追加されたメンバー情報
 */
/**
 * サブスクリプションを解約（期間終了時にキャンセル）
 */
export const cancelSubscription = async (): Promise<{ cancel_at: number; cancel_at_period_end: boolean }> => {
  try {
    const response = await axiosInstance.post('/organizations/manage/cancel-subscription');
    return response.data;
  } catch (error) {
    throw handleApiError(error, '解約処理に失敗しました');
  }
};

/**
 * サブスクリプション解約を取り消す
 */
export const resumeSubscription = async (): Promise<void> => {
  try {
    await axiosInstance.post('/organizations/manage/resume-subscription');
  } catch (error) {
    throw handleApiError(error, '解約取り消しに失敗しました');
  }
};

/**
 * アカウントをすべて削除する（組織を即時停止し、Stripe の契約があれば即時解約する。全員ログインできなくなる）
 */
export const cancelOrganization = async (): Promise<{ message: string; stripe_subscription_cancelled: boolean }> => {
  try {
    const response = await axiosInstance.post('/organizations/manage/cancel-organization');
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'アカウントの削除に失敗しました');
  }
};

// 一括招待の入力。role は API 側で保存先の列が無く捨てられるため受け付けない。
export interface BulkMemberInput {
  name?: string;
  email: string;
}

// 件ごとの結果。1件失敗しても残りは続行されるので、成否は件ごとに見る。
export interface BulkMemberResult {
  email: string;
  ok: boolean;
  message: string;
}

export interface BulkAddMembersResponse {
  results: BulkMemberResult[];
}

/**
 * メンバーをまとめて招待する（最大20件）。
 * 1件ずつ POST していたのを 1 リクエストにまとめる。
 */
export const addMembersBulk = async (
  members: BulkMemberInput[]
): Promise<BulkAddMembersResponse> => {
  try {
    const response = await axiosInstance.post('/organizations/manage/members', { members });
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'メンバーの一括追加に失敗しました');
  }
};

export const addNewMember = async (
  name: string,
  email: string,
  role: 'leader' | 'member'
): Promise<OrganizationMember> => {
  try {
    // 組織管理エンドポイントを使用して直接メンバーを追加
    const response = await axiosInstance.post('/organizations/manage/members', {
      name,
      email,
      role
    });
    return response.data.member;
  } catch (error) {
    throw handleApiError(error, 'メンバーの追加に失敗しました');
  }
};
